import axios, { AxiosInstance, AxiosResponse } from 'axios';
import toast from 'react-hot-toast';

// API Configuration
export const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 150000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// User-friendly error messages based on status code
const getErrorMessage = (status: number, serverMessage?: string): string => {
  switch (status) {
    case 400:
      return serverMessage || 'Please check your input and try again.';
    case 401:
      return 'Your session has expired. Please log in again.';
    case 403:
      return 'You don\'t have permission to perform this action.';
    case 404:
      return 'The requested resource was not found. Please try again later.';
    case 408:
      return 'The request timed out. Please check your connection and try again.';
    case 429:
      return 'Too many requests. Please wait a moment and try again.';
    case 500:
      return 'Something went wrong on our end. Please try again later.';
    case 502:
    case 503:
    case 504:
      return 'Our servers are temporarily unavailable. Please try again in a few minutes.';
    default:
      return serverMessage || 'Something went wrong. Please try again.';
  }
};

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 errors (unauthorized)
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refreshToken,
          });

          const { accessToken } = response.data.data;
          localStorage.setItem('token', accessToken);

          // Retry the original request with new token
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return apiClient(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed, redirect to login
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    const status = error.response?.status || 0;
    const serverMessage =
      error.response?.data?.message || error.response?.data?.error;
    const errorMessage = getErrorMessage(status, serverMessage);
    const method = originalRequest?.method?.toUpperCase() || 'GET';
    const silentErrors = [401, 403];
    const isBackgroundGet = method === 'GET' && status === 404;
    if (!silentErrors.includes(status) && !isBackgroundGet) {
      // Surface the request id so users can quote it to support / paste
      // it into /admin/system/errors. Server now tags every response
      // with X-Request-Id (and 5xx bodies include `requestId`).
      const requestId =
        error.response?.data?.requestId ||
        error.response?.headers?.['x-request-id'] ||
        error.response?.headers?.['X-Request-Id'];
      const toastMessage = requestId && status >= 500
        ? `${errorMessage} (Ref: ${requestId})`
        : errorMessage;
      toast.error(toastMessage);
    }

    return Promise.reject(error);
  }
);

export default apiClient;
